import { siteApiSlice } from "../../app/siteApiSlice";
import { SelectListItem } from "../../components/input/SelectList";
import { DataTablesViewModel, DataTablesQueryModel } from "../../interfaces/dataTables";
import { StimulusMaterialViewModel } from "../stimulusMaterial/stimulusMaterialApiSlice";
import { ContentFormat } from "./drafting/draftingApiSlice";

export enum WritingAction {
	Organize = "Organize",
	Write = "Write",
	ScoreReport = "ScoreReport",
	NotStarted = "NotStarted",
	PastDue = "PastDue",
	StartOrganizing = "StartOrganizing",
	StartWriting = "StartWriting",
	ReviseEssay = "ReviseEssay",
}

export enum DraftStatus {
	Scored = "Scored",
	Incomplete = "Incomplete",
	Deleted = "Deleted",
	ScoringFailed = "ScoringFailed",
	Revised = "Revised",
	Formatting = "Formatting",
	Finalized = "Finalized",
	NotStarted = "NotStarted",
}

export interface WritingHistoryViewModel {
	essayId: number;
	action: WritingAction;
	actionDraftId: number;
	actionUrl: string;
	promptName: string;
	title: string;
	step: string;
	remainingRevisions: number;
	essayCreateDate: string;
	lastDraftCreateDate: string;
	lastDraftUpdateDate: string;
	lastDraftStatus: DraftStatus;
	lastDraftDescription: string;
	peerReview: string;
	messages: string;
	messageCount: number;
	unreadMessageCount: number;
	dueDate: string | "";
	dueDateEnabled: boolean;
	dueDateExceeded: boolean;
	dueDateRequired: boolean;
	dueDateCanAccessPrompt: boolean;
}

export interface WritingHistoryQueryModel extends DataTablesQueryModel {
	promptFilter: string;
}

export interface PromptModel {
	promptId: number;
	name: string;
	renderedText: string;
	genre: string;
	contentAreas: string[];
	stimulusMaterials: StimulusMaterialViewModel[];
	signLanguageVideoURL: string;
}

export interface CoursePromptModel {
	id: number;
	prompt: PromptModel;
	courseId: number;
	isEnabled: boolean;
	statusDateUtc: string;
	isAssignedToUser: boolean;
	dueDateUtc?: string;
	isOverdue: boolean;
	isDueDateRequired: boolean;
	allowPrewriting: boolean;
	isOrganizerRequired: boolean;
	draftCountLimit: number;
}

export interface PromptWritingModel {
	promptId: number;
	promptName: string;
	essayCount: number;
	lastEssayId: number;
	draftCount: number;
	totalDraftCount: number;
	lastDraftId: number;
	draftStatus: DraftStatus;
	updateDateUtc: string;
	messageCount: number;
	newMessageCount: number;
	action: WritingAction;
	actionId?: number;
}

export interface CourseWritingActivityModel {
	writingActivity: CourseWritingActivityPromptModel[];
	writers: SelectListItem[];
}

export interface CourseWritingActivityPromptModel {
	promptId: number;
	promptName: string;
	genreName: string;
	genreMaxScore: number;
	contentAreas: string[];
	isAssigned: boolean;
	dueDate: string;
	isPastDue: boolean;
	lastUpdateDate?: string;
	reviewsCompletedCount: number;
	reviewsRequestedCount: number;
	newMessagesCount: number;
	totalMessagesCount: number;
	startedCount: number;
	scoredCount: number;
	totalCount: number;
	completed: boolean;
	essays: CourseWritingActivityEssayModel[];
}

export interface CourseWritingActivityEssayModel {
	essayId: number;
	writerId: number;
	writerFormalName: string;
	writerInitials: string;
	writerHasImage: boolean;
	lastUpdateDate?: string;
	status: DraftStatus;
	statusDescription: string;
	step: string;
	score?: number;
	reviewsCompletedCount: number;
	reviewsRequestedCount: number;
	newMessagesCount: number;
	totalMessagesCount: number;
	printable: boolean;
	action: WritingAction;
	actionId?: number;
}

export interface PrintPromptLastEssaysViewModel {
	essays: LastEssaysPrintModel[];
	courseName: string;
	promptName: string;
}

export interface LastEssaysPrintModel {
	writerFullName: string;
	essayContent: string;
	essayFormat: ContentFormat;
	essayMarkup: string;
	date: string;
}

export const writingApiSlice = siteApiSlice.injectEndpoints({
	endpoints: (build) => ({
		writingHistory: build.query<
			DataTablesViewModel<WritingHistoryViewModel>,
			WritingHistoryQueryModel
		>({
			query: (q) => {
				const { iSortCol_0, sSortDir_0, iDisplayStart, iDisplayLength, promptFilter } = q;
				return {
					url: `/Home/WriterAjaxHandler`,
					params: {
						iSortCol_0,
						sSortDir_0,
						iDisplayStart,
						iDisplayLength,
						promptFilter,
					},
				};
			},
			providesTags: ["CurrentCourse", "WritingHistory"],
		}),
		getPrompts: build.query<CoursePromptModel[], void>({
			query: () => ({
				url: `/Writing/GetPrompts`,
			}),
			providesTags: ["CurrentCourse", "Prompts", "WritingHistory"],
		}),
		getWriting: build.query<PromptWritingModel[], void>({
			query: () => ({
				url: `/Writing/GetWriting`,
			}),
			providesTags: ["CurrentCourse", "Prompts", "WritingHistory"],
		}),
		getCourseWritingActivity: build.query<CourseWritingActivityModel, number | null>({
			query: (writerId) => ({
				url: `/Writing/GetCourseWritingActivity`,
				params: { writerId },
			}),
			providesTags: ["CurrentCourse", "WritingHistory"],
		}),
		printLastScoredEssays: build.query<PrintPromptLastEssaysViewModel, number>({
			query: (promptId) => ({
				url: `/prompt-essays/${promptId}`,
			}),
			providesTags: ["CurrentCourse", "WritingHistory"],
		}),
	}),
});

export const {
	useWritingHistoryQuery,
	useLazyWritingHistoryQuery,
	useGetPromptsQuery,
	useGetWritingQuery,
	useGetCourseWritingActivityQuery,
	useLazyPrintLastScoredEssaysQuery,
} = writingApiSlice;
